<template>
    <div>
        <div class="row gutter-xs">
            <div class="panel panel-default">
                <div class="panel-body">
                    <div class="flex flex-col items-center justify-between md:flex-row">
                        <div class="mb-6 md:mb-0 md:w-1/3 w-full">
                            <div class="btn-group btn-group-sm" role="group">
                                <button @click="filtersPanelIsOpened = !filtersPanelIsOpened" class="btn" :class="filtersPanelIsOpened ? 'btn-primary' : 'btn-outline-primary'" type="button">
                                    <span class="icon icon-filter"></span> {{ $t('globals.filters') }}
                                    <span v-if="filtersCount > 0" class="badge">{{ filtersCount }}</span>
                                </button>
                            </div>
                        </div>
                        <div class="flex justify-center lg:justify-center md:justify-end md:w-1/3 w-full">
                            <div class="form-group form-group-sm">
                                <div class="input-with-icon mx-auto w-300px">
                                    <input class="form-control input-sm" type="search" v-model="searchQuery" :placeholder="$t('users.kiosk_search_placeholder')">
                                    <span class="icon icon-search input-icon"></span>
                                </div>
                            </div>
                        </div>
                        <div class="hidden lg:block lg:w-1/3"></div>
                    </div>
                    <div v-if="filtersPanelIsOpened" class="bg-gray-100 panel mt-5px">
                        <div class="panel-body text-center">
                            <form class="form form-horizontal">
                                <div class="row">
                                    <div class="col-xs-6 form-group">
                                        <label class="col-sm-3 control-label" for="form-control-class">{{ $t('users.role') }}</label>
                                        <div class="col-sm-9 m-t flex">
                                            <label class="custom-control custom-control-primary custom-radio">
                                                <input class="custom-control-input" id="form-control-class" type="radio" :value="false" v-model="filters.ownersOnly">
                                                <span class="custom-control-indicator"></span>
                                                <span class="custom-control-label">{{ $t('users.any_role') }}</span>
                                            </label>
                                            <label class="custom-control custom-control-primary custom-radio">
                                                <input class="custom-control-input" id="form-control-class" type="radio" :value="true" v-model="filters.ownersOnly">
                                                <span class="custom-control-indicator"></span>
                                                <span class="custom-control-label">{{ $t('users.only_organization_owners') }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="form-group mb-0 space-x-2">
                                        <button @click="onFiltersSubmit" class="btn btn-outline-primary btn-sm" type="button">
                                            {{ $t('tables.apply_filters') }}
                                        </button>
                                        <button @click="onFiltersReset" type="button" class="btn btn-default btn-sm hover:border-gray-300 hover:bg-gray-300 border-gray-100 bg-gray-100 text-gray-800">
                                            {{ $t('tables.reset_filters') }}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <vue-good-table
                        mode="remote"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        @on-search="onSearchChange"
                        :search-options="{ enabled: true, externalQuery: serverParams.search }"
                        :sort-options="{
                            enabled: true,
                            initialSortBy: { field: 'created_at', type: 'desc' },
                        }"
                        :pagination-options="{
                            enabled: true,
                            perPage: 20,
                            setCurrentPage: table.currentPage,
                            nextLabel: $t('tables.next'),
                            prevLabel: $t('tables.previous'),
                            rowsPerPageLabel: $t('tables.rows_per_page'),
                            ofLabel: $t('tables.of'),
                            pageLabel: $t('tables.page'),
                            allLabel: $t('tables.all'),
                        }"
                        :columns="table.columns"
                        :rows="table.rows"
                        :total-rows="table.totalRecords"
                        :is-loading="table.isLoading"
                        style-class="table vgt-table striped">
                        <div slot="emptystate">
                            <div class="row text-center p-y-md">
                                <div class="col-xs-8 col-xs-offset-2">
                                    <p>{{ $t('tables.no_records_found') }}</p>
                                </div>
                            </div>
                        </div>
                        <template slot="table-column" slot-scope="props">
                            <span v-if="props.column.field === 'last_active_at'" class="inline-flex items-center">
                                {{ props.column.label }}
                                <span class="icon icon-question-circle icon-fw ml-1"
                                    v-tooltip="{
                                        content: $t('users.last_active_at_tip', {
                                            mailIcon: '<span class=&quot;icon icon-send&quot;></span>',
                                            lineBreak: '<br/>',
                                            notificationIcon: '<span class=&quot;icon icon-bell&quot;></span>'
                                        }),
                                        placement: 'top'
                                    }">
                                </span>
                            </span>
                            <span v-else v-html="props.column.label"></span>
                        </template>
                        <template slot="table-row" slot-scope="props">
                            <template v-if="props.column.field === 'full_name'">
                                <div class="flex items-center">
                                    <div class="mr-3">
                                        <avatar :user="props.row" :size="'32px'"></avatar>
                                    </div>
                                    <div class="flex flex-col">
                                        <div class="font-bold">
                                            {{ props.row.full_name }}
                                        </div>
                                        <div>
                                            {{ props.row.email }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'organization'">
                                <div v-if="props.row.organization" class="flex items-center">
                                    <div class="mr-3">
                                        <organization-logo :organization="props.row.organization" :size="'32px'"></organization-logo>
                                    </div>
                                    <div class="" v-html="props.row.organization.name"></div>
                                </div>
                                <template v-else>
                                    {{ $t('globals.n_a') }}
                                </template>
                            </template>
                            <template v-else-if="props.column.field === 'created_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.created_at) }}
                                    </div>
                                    <div class="font-medium text-2xs text-gray-table-light">
                                        ({{ props.row.readable_created_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'last_active_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.last_active_at) }}
                                    </div>
                                    <div class="font-medium text-2xs text-gray-table-light">
                                        ({{ props.row.readable_last_active_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field == 'actions'">
                                <template v-if="userIsDeveloper">
                                    <div class="basic-btn-toolbar btn-toolbar flex justify-center" role="toolbar">
                                        <div class="btn-group btn-group-xs" role="group">
                                            <button @click="onImpersonateUser(props.row)" class="btn btn-default" v-tooltip="{ content: $t('users.impersonate'), placement: 'top' }">
                                                <span class="icon icon-eye"></span>
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </template>
                            <span v-else v-html="props.formattedRow[props.column.field]"></span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';
import Avatar from '../../user/partials/avatar.vue';
import OrganizationLogo from '../../user/partials/organization-logo.vue';

export default {
    components: {
        'avatar': Avatar,
        'organization-logo': OrganizationLogo,
    },

    props: {
        initialFilters: {
            type: Object,
            default: function() {
                return {
                    ownersOnly: false,
                };
            },
        },
        initialSort: {
            type: Array,
            default: function() {
                return [
                    { column: 'created_at', dir: 'desc' },
                ];
            },
        },
    },

    data() {
        return {
            userIsDeveloper: false,
            filtersPanelIsOpened: false,
            filters: Vue.util.extend({}, this.initialFilters),
            table: {
                columns: [
                    {
                        label: this.$t('globals.name'),
                        field: 'full_name',
                        html: true,
                    },
                    {
                        label: this.$t('globals.organization'),
                        field: 'organization',
                    },
                    {
                        label: this.$t('users.signed_up_at'),
                        field: 'created_at',
                        html: true,
                    },
                    {
                        label: this.$t('users.last_active'),
                        field: 'last_active_at',
                        html: true,
                    },
                    {
                        label: this.$t('globals.actions'),
                        field: 'actions',
                        html: true,
                        sortable: false,
                        hidden: !window.Globals.isDeveloper,
                        thClass: 'text-center',
                    },
                ],
                rows: [],
                totalRecords: 0,
                currentPage: 1,
                isLoading: false,
            },
            serverParams: {
                order: [],
                start: 0,
                length: 20,
                search: '',
            },
            searchQuery: '',
            form: new Form({
                user_id: '',
            }),
        };
    },

    created() {
        this.userIsDeveloper = window.Globals.isDeveloper;
    },

    computed: {
        filtersCount() {
            let count = 0;
            if (this.filters.ownersOnly !== false) {
                count++;
            }

            return count;
        },
    },

    watch: {
        searchQuery: Globals.debounce(function (value) {
            this.serverParams.search = value;
        }, 750)
    },

    methods: {
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChange(params) {
            this.table.currentPage = params.currentPage;
            let start = 0;
            if (params.currentPage > 1) {
                start = (params.currentPage - 1) * params.currentPerPage;
            }
            this.updateParams({ start: start });
            this.loadItems();
        },

        onPerPageChange(params) {
            this.updateParams({ length: params.currentPerPage });
            this.loadItems();
        },

        onSortChange(params) {
            const newOrder = [];
            newOrder.push({ 'column': _.head(params)['field'], 'dir': _.head(params)['type'] });
            this.updateParams({ order: newOrder });
            this.loadItems();
        },

        onSearchChange(params) {
            this.updateParams({ search: params.searchTerm });
            this.loadItems();
        },

        goToFirstPage() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0 });
            this.loadItems();
        },

        loadItems() {
            this.table.isLoading = true;

            const filters = {
                only_owners: this.filters.ownersOnly,
            };
            const queryParams = Object.assign({}, this.serverParams, filters);

            axios.get('/data/kiosk/users', { params: queryParams })
                .then(response => {
                    this.table.totalRecords = response.data.recordsFiltered;
                    this.table.rows = response.data.data;
                    this.table.isLoading = false;
                });
        },

        onFiltersSubmit() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0, order: this.initialSort });
            this.loadItems();
        },

        onFiltersReset() {
            this.filters = Vue.util.extend({}, this.initialFilters);
            this.onFiltersSubmit();
            this.filtersPanelIsOpened = false;
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y HH:mm');
        },

        onImpersonateUser(user) {
            this.form.user_id = user.id;

            Globals.post('/kiosk/impersonate', this.form)
                .then(() => window.location.href = '/dashboard')
                .catch(() => {
                    const message = this.form.errors.any() > 0 ? this.form.errors.flatten()[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                })
                .finally(() => this.resetForm());
        },

        resetForm() {
            this.form = new Form({
                user_id: '',
            });
        },
    },
};
</script>
